import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    IconButton,
    Box,
    Tabs,
    Tab,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Switch,
    FormControlLabel,
    Avatar
} from '@mui/material';
import {
    Close as CloseIcon,
    Settings as SettingsIcon,
    Save as SaveIcon,
    Business as BusinessIcon,
    PhotoCamera
} from '@mui/icons-material';

/**
 * Dialog PARAMETRES
 * Permet de personnaliser Structure / Identification / Tarifs
 */
const ParametresDialog = ({ open, onClose, client, onSave }) => {
    const [tabValue, setTabValue] = useState(0);
    const [formData, setFormData] = useState({
        // Structure
        typeStructure: '',
        secteurActivite: '',
        formeJuridique: '',
        numeroSiret: '',

        // Identification
        raisonSociale: '',
        nomCommercial: '',
        adresseSiege: '',
        codePostal: '',
        ville: '',
        pays: '',

        // Tarifs
        categorieTarifaire: '',
        remiseGenerale: 0,
        conditionPaiement: '',
        delaiPaiement: 30,

        // Gestion
        encoursMaximum: 0,
        miseEnSommeil: false,
        contact: '',
        logo: null
    });
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoChanged, setLogoChanged] = useState(false);

    useEffect(() => {
        if (open && client) {
            // Charger les données du client
            setFormData({
                typeStructure: client.typeStructure || '',
                secteurActivite: client.secteurActivite || '',
                formeJuridique: client.formeJuridique || '',
                numeroSiret: client.numeroSiret || '',
                raisonSociale: client.nom || '',
                nomCommercial: client.nomCommercial || '',
                adresseSiege: client.adresse || '',
                codePostal: client.codePostal || '',
                ville: client.ville || '',
                pays: client.pays || 'Sénégal',
                categorieTarifaire: client.categorieTarifaire || 'Standard',
                remiseGenerale: client.remiseGenerale || 0,
                conditionPaiement: client.conditionPaiement || 'Comptant',
                delaiPaiement: client.delaiPaiement || 30,
                encoursMaximum: client.encoursMaximum || 0,
                miseEnSommeil: client.miseEnSommeil || false,
                contact: client.contact || '',
                logo: client.logo || null
            });
            setLogoPreview(client.logo || null);
            setLogoChanged(false);
        }
    }, [open, client]);

    const compressImage = (file, maxWidth = 800, quality = 0.7) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                    resolve(compressedBase64);
                };
                img.onerror = reject;
            };
            reader.onerror = reject;
        });
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const compressedLogo = await compressImage(file, 800, 0.7);
                setLogoPreview(compressedLogo);
                setFormData(prev => ({ ...prev, logo: compressedLogo }));
                setLogoChanged(true);
            } catch (error) {
                console.error('Erreur compression image:', error);
            }
        }
    };

    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData({
            ...formData,
            [field]: value
        });
    };

    const handleSave = () => {
        if (onSave) {
            const dataToSave = { ...formData };
            if (!logoChanged) {
                delete dataToSave.logo;
            }
            onSave(dataToSave);
        }
        onClose();
    };

    if (!client) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    minHeight: '600px'
                }
            }}
        >
            <DialogTitle sx={{
                position: 'relative',
                pb: 1,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                <SettingsIcon color="info" />
                PARAMETRES - {client.structure ? client.nom : `${client.prenom} ${client.nom}`}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={tabValue}
                    onChange={(e, newValue) => setTabValue(newValue)}
                    variant="fullWidth"
                >
                    <Tab label="Structure" />
                    <Tab label="Identification" />
                    <Tab label="Tarifs" />
                    <Tab label="Gestion" />
                </Tabs>
            </Box>

            <DialogContent sx={{ pt: 3 }}>
                {/* Onglet Structure */}
                {tabValue === 0 && (
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">
                            Configuration de la Structure
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Type de Structure</InputLabel>
                                    <Select
                                        value={formData.typeStructure}
                                        onChange={handleChange('typeStructure')}
                                        label="Type de Structure"
                                    >
                                        <MenuItem value="Entreprise">Entreprise</MenuItem>
                                        <MenuItem value="Association">Association</MenuItem>
                                        <MenuItem value="Administration">Administration</MenuItem>
                                        <MenuItem value="Particulier">Particulier</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Secteur d'Activité"
                                    value={formData.secteurActivite}
                                    onChange={handleChange('secteurActivite')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Forme Juridique</InputLabel>
                                    <Select
                                        value={formData.formeJuridique}
                                        onChange={handleChange('formeJuridique')}
                                        label="Forme Juridique"
                                    >
                                        <MenuItem value="SARL">SARL</MenuItem>
                                        <MenuItem value="SA">SA</MenuItem>
                                        <MenuItem value="SAS">SAS</MenuItem>
                                        <MenuItem value="EI">Entreprise Individuelle</MenuItem>
                                        <MenuItem value="GIE">GIE</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Numéro SIRET / NINEA"
                                    value={formData.numeroSiret}
                                    onChange={handleChange('numeroSiret')}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Onglet Identification */}
                {tabValue === 1 && (
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">
                            Informations d'Identification
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Avatar
                                        src={logoPreview}
                                        sx={{ width: 100, height: 100, mx: 'auto', mb: 1, border: '1px solid #ddd' }}
                                    >
                                        <BusinessIcon sx={{ fontSize: 60 }} />
                                    </Avatar>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        size="small"
                                        startIcon={<PhotoCamera color="info" />}
                                    >
                                        Changer Logo
                                        <input hidden accept="image/*" type="file" onChange={handleLogoChange} />
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    fullWidth
                                    label="Raison Sociale"
                                    value={formData.raisonSociale}
                                    onChange={handleChange('raisonSociale')}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Nom Commercial"
                                    value={formData.nomCommercial}
                                    onChange={handleChange('nomCommercial')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Adresse du Siège"
                                    value={formData.adresseSiege}
                                    onChange={handleChange('adresseSiege')}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Code Postal"
                                    value={formData.codePostal}
                                    onChange={handleChange('codePostal')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Ville"
                                    value={formData.ville}
                                    onChange={handleChange('ville')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Pays"
                                    value={formData.pays}
                                    onChange={handleChange('pays')}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Onglet Tarifs */}
                {tabValue === 2 && (
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">
                            Grille Tarifaire
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Catégorie Tarifaire</InputLabel>
                                    <Select
                                        value={formData.categorieTarifaire}
                                        onChange={handleChange('categorieTarifaire')}
                                        label="Catégorie Tarifaire"
                                    >
                                        <MenuItem value="Standard">Standard</MenuItem>
                                        <MenuItem value="Premium">Premium</MenuItem>
                                        <MenuItem value="VIP">VIP</MenuItem>
                                        <MenuItem value="Partenaire">Partenaire</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Remise Générale (%)"
                                    value={formData.remiseGenerale}
                                    onChange={handleChange('remiseGenerale')}
                                    inputProps={{ min: 0, max: 100 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Condition de Paiement</InputLabel>
                                    <Select
                                        value={formData.conditionPaiement}
                                        onChange={handleChange('conditionPaiement')}
                                        label="Condition de Paiement"
                                    >
                                        <MenuItem value="Comptant">Comptant</MenuItem>
                                        <MenuItem value="A terme">À terme</MenuItem>
                                        <MenuItem value="Acompte">Acompte 50%</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Délai de Paiement (jours)"
                                    value={formData.delaiPaiement}
                                    onChange={handleChange('delaiPaiement')}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
                {/* Onglet Gestion */}
                {tabValue === 3 && (
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">
                            Paramètres de Gestion
                        </Typography>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Encours maximum autorisé"
                                    value={formData.encoursMaximum}
                                    onChange={handleChange('encoursMaximum')}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    helperText="Montant maximal de crédit autorisé pour ce client"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.miseEnSommeil}
                                            onChange={handleChange('miseEnSommeil')}
                                            color="error"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1">Mise en sommeil</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Désactive temporairement le compte client
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Grid>
                            {client.structure && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Contact (Interlocuteur)"
                                        value={formData.contact}
                                        onChange={handleChange('contact')}
                                        placeholder="Nom de la personne à contacter"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
                <Button onClick={onClose} variant="outlined">
                    Annuler
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        onClick={() => {
                            // "Interroger" button behavior
                            // We can emit a specific event or call a callback, 
                            // but simpler is to call a new prop 'onInterroger'
                            if (window.onInterrogerClient) {
                                window.onInterrogerClient(client.id);
                            }
                        }}
                        variant="outlined"
                        color="secondary"
                    >
                        Interroger
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                    >
                        Enregistrer
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ParametresDialog;
